'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSWRConfig } from 'swr';
import {
  CircleNotchIcon,
  CreditCardIcon,
  PlusIcon,
} from '@phosphor-icons/react';
import { PageSkeleton } from '@/components/PageSkeleton';
import { ErrorState } from '@/components/ErrorState';
import {
  FINANCIAL_DATA_KEY_PREFIXES,
  revalidateTransactionCaches,
} from '@/hooks/queries/swrConfig';
import { useFinance } from '@/hooks/useFinance';
import type { Account } from '@/types';
import { PageHeader } from './PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/useToast';
import { track } from '@/lib/telemetry';
import { notifyError } from '@/lib/notify-error';
import { ConnectionsMetrics } from './ConnectionsMetrics';
import { ConnectionCard, type ConnectionCardItem } from './ConnectionCard';
import { ConnectionUpdateDialog, type ConnectFlowState, type ImportState } from './ConnectionUpdateDialog';
import { ImportProgress } from './ImportProgress';
import {
  deriveImportSteps,
  buildActionRequiredAlert,
  buildErrorAlert,
  buildTimeoutAlert,
} from './ImportProgress.helpers';

interface ItemWithAccounts extends ConnectionCardItem {
  executionStatus?: string;
  lastUpdatedAt?: string;
  consentSignedAt?: string;
  consecutiveFailedLoginAttempts?: number;
  accounts: Account[];
}

const ACTIVE_SYNC_STATUSES = ['UPDATING', 'MERGING'];
const ACTION_REQUIRED_STATUSES = ['WAITING_USER_INPUT', 'WAITING_USER_ACTION'];
const CONNECTION_IMPORT_POLL_MS = 3000;
const CONNECTION_IMPORT_TIMEOUT_MS = 120_000;
const CONNECTION_SUCCESS_VISIBLE_MS = 5000;

export function Connections() {
  const { showToast } = useToast();
  const { mutate: mutateGlobal } = useSWRConfig();
  const { fetchItems, fetchAccounts, deleteItem } = useFinance();

  const [items, setItems] = useState<ItemWithAccounts[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redirectError, setRedirectError] = useState<string | null>(null);
  const [isCreatingItem, setIsCreatingItem] = useState(false);
  const [pendingPluggyItemId, setPendingPluggyItemId] = useState<string | null>(null);
  const [connectionImportStartedAt, setConnectionImportStartedAt] = useState<number | null>(null);
  const [connectionImportTimedOut, setConnectionImportTimedOut] = useState(false);
  const [completedConnectionName, setCompletedConnectionName] = useState<string | null>(null);
  const [disconnectingItem, setDisconnectingItem] = useState<ItemWithAccounts | null>(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [connectFlow, setConnectFlow] = useState<ConnectFlowState>({ open: false });
  // pluggyItemId whose blocked outcome (action_required/error/timeout) was already
  // reported — the poll keeps running for UI purposes, but analytics must emit ONE
  // terminal `connect.import_finished` per import attempt, not one per tick.
  const importOutcomeTrackedRef = useRef<string | null>(null);

  // Syncs rewrite transactions — the infinite /transactions list needs its page
  // caches reset too, or it serves the pre-sync rows until a full reload.
  const revalidateFinancialCaches = useCallback(
    () => revalidateTransactionCaches(mutateGlobal, FINANCIAL_DATA_KEY_PREFIXES),
    [mutateGlobal],
  );

  const loadData = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    if (!silent) {
      setIsLoading(true);
      setError(null);
    }

    try {
      const itemsData = await fetchItems();

      let itemsWithAccounts: ItemWithAccounts[] = itemsData.map((item) => ({
        id: item.id,
        pluggyItemId: item.pluggyItemId,
        connectorId: item.connectorId,
        connectorName: item.connectorName,
        connectorImage: item.connectorImage,
        connectorPrimaryColor: item.connectorPrimaryColor,
        status: item.status,
        executionStatus: item.executionStatus,
        updatedAt: item.updatedAt,
        lastUpdatedAt: item.lastUpdatedAt,
        nextAutoSyncAt: item.nextAutoSyncAt,
        consentExpiresAt: item.consentExpiresAt,
        consentSignedAt: item.consentSignedAt,
        consecutiveFailedLoginAttempts: item.consecutiveFailedLoginAttempts,
        errorCode: item.errorCode,
        errorMessage: item.errorMessage,
        products: item.products || [],
        accounts: item.accounts || [],
        connectorHealth: item.connectorHealth,
        connectionIssue: item.connectionIssue,
      }));

      if (itemsWithAccounts.length > 0 && itemsWithAccounts.every((item) => item.accounts.length === 0)) {
        try {
          const accounts = await fetchAccounts();
          const accountsByItem = new Map<string, Account[]>();
          for (const account of accounts) {
            const list = accountsByItem.get(account.itemId) || [];
            list.push(account);
            accountsByItem.set(account.itemId, list);
          }
          itemsWithAccounts = itemsWithAccounts.map((item) => ({
            ...item,
            accounts: accountsByItem.get(item.id) || [],
          }));
        } catch (accountsErr) {
          console.warn('Failed to enrich items with accounts:', accountsErr);
        }
      }

      setItems(itemsWithAccounts);
      return itemsWithAccounts;
    } catch (err) {
      console.error('Error loading data:', err);
      if (!silent) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
      }
      return [];
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [fetchItems, fetchAccounts]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const finishPendingImportIfPresent = useCallback((loadedItems: ItemWithAccounts[], pluggyItemId: string, importStartedAt?: number | null) => {
    const importedItem = loadedItems.find((item) => item.pluggyItemId === pluggyItemId);
    if (!importedItem) return false;
    if (ACTION_REQUIRED_STATUSES.includes(importedItem.status ?? '') || importedItem.status === 'LOGIN_ERROR') {
      return false;
    }
    if (importedItem.accounts.length === 0) return false;

    track('connect.import_finished', {
      status: 'completed',
      duration_ms: importStartedAt ? Date.now() - importStartedAt : undefined,
    });
    setPendingPluggyItemId(null);
    setConnectionImportStartedAt(null);
    setConnectionImportTimedOut(false);
    setIsCreatingItem(false);
    setCompletedConnectionName(importedItem.connectorName);
    return true;
  }, []);

  useEffect(() => {
    const hasTransient = items.some((item) => item.status && ACTIVE_SYNC_STATUSES.includes(item.status));
    if (!hasTransient) return;
    const startTime = Date.now();
    const interval = setInterval(() => {
      if (Date.now() - startTime > 120_000) {
        clearInterval(interval);
        return;
      }
      void loadData({ silent: true }).then((loadedItems) => {
        const stillSyncing = loadedItems.some((item) =>
          item.status && ACTIVE_SYNC_STATUSES.includes(item.status));
        if (!stillSyncing) void revalidateFinancialCaches();
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [items, loadData, revalidateFinancialCaches]);

  useEffect(() => {
    if (!completedConnectionName) return;
    const timeout = window.setTimeout(() => setCompletedConnectionName(null), CONNECTION_SUCCESS_VISIBLE_MS);
    return () => window.clearTimeout(timeout);
  }, [completedConnectionName]);

  const mobileRedirectProcessed = useRef(false);

  useEffect(() => {
    if (mobileRedirectProcessed.current || typeof window === 'undefined') return;
    mobileRedirectProcessed.current = true;

    const paramKeys = ['itemId', 'pluggyItemId', 'pluggyConnectItemId', 'item_id'];
    const normalizeItemId = (value: string | null) => {
      if (!value) return null;
      const trimmed = value.trim().replace(/^['"]|['"]$/g, '');
      if (!trimmed) return null;
      const lowered = trimmed.toLowerCase();
      if (lowered === 'null' || lowered === 'undefined') return null;
      return trimmed;
    };
    const isValidPluggyItemId = (value: string) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

    const searchParams = new URLSearchParams(window.location.search);
    let searchValue: string | null = null;
    let hadSearchParam = false;
    for (const key of paramKeys) {
      if (searchParams.has(key)) {
        hadSearchParam = true;
        const value = normalizeItemId(searchParams.get(key));
        if (value) {
          searchValue = value;
          break;
        }
      }
    }

    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    let hashValue: string | null = null;
    let hadHashParam = false;
    for (const key of paramKeys) {
      if (hashParams.has(key)) {
        hadHashParam = true;
        const value = normalizeItemId(hashParams.get(key));
        if (value) {
          hashValue = value;
          break;
        }
      }
    }

    for (const key of paramKeys) {
      if (searchParams.has(key)) searchParams.delete(key);
      if (hashParams.has(key)) hashParams.delete(key);
    }

    if (hadSearchParam || hadHashParam) {
      const newSearch = searchParams.toString();
      const newHash = hashParams.toString();
      const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${newHash ? `#${newHash}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    }

    const itemId = searchValue ?? hashValue;
    if (itemId && isValidPluggyItemId(itemId)) {
      // Mobile redirect return — the widget completed out-of-page, so the
      // in-page success handler never ran.
      track('action.connect.complete', {});
      setRedirectError(null);
      setConnectionImportTimedOut(false);
      setCompletedConnectionName(null);
      setPendingPluggyItemId(itemId);
      setConnectionImportStartedAt(Date.now());
      setIsCreatingItem(true);
      void loadData({ silent: true }).then((loadedItems) => {
        if (finishPendingImportIfPresent(loadedItems, itemId)) {
          void revalidateFinancialCaches();
        }
      });
    } else if (hadSearchParam || hadHashParam) {
      setIsCreatingItem(false);
      setPendingPluggyItemId(null);
      setConnectionImportStartedAt(null);
      setConnectionImportTimedOut(false);
      setRedirectError('Falha ao completar a conexão. Tente conectar novamente.');
    }
  }, [finishPendingImportIfPresent, loadData, revalidateFinancialCaches]);

  useEffect(() => {
    if (!pendingPluggyItemId || !connectionImportStartedAt) return;
    let cancelled = false;
    const poll = async () => {
      const loadedItems = await loadData({ silent: true });
      if (cancelled) return;
      if (finishPendingImportIfPresent(loadedItems, pendingPluggyItemId, connectionImportStartedAt)) {
        void revalidateFinancialCaches();
        return;
      }
      // An import stuck on user action or bad credentials is a terminal outcome
      // for analytics even though the UI keeps polling (the reauth flow reopens
      // via its own effect) — otherwise it would be misreported as `timeout`.
      const pendingItem = loadedItems.find((item) => item.pluggyItemId === pendingPluggyItemId);
      const blockedStatus = pendingItem?.status && ACTION_REQUIRED_STATUSES.includes(pendingItem.status)
        ? 'action_required' as const
        : pendingItem?.status === 'LOGIN_ERROR'
          ? 'error' as const
          : null;
      if (blockedStatus && importOutcomeTrackedRef.current !== pendingPluggyItemId) {
        importOutcomeTrackedRef.current = pendingPluggyItemId;
        track('connect.import_finished', {
          status: blockedStatus,
          duration_ms: Date.now() - connectionImportStartedAt,
        });
      }
      if (Date.now() - connectionImportStartedAt > CONNECTION_IMPORT_TIMEOUT_MS) {
        if (importOutcomeTrackedRef.current !== pendingPluggyItemId) {
          track('connect.import_finished', {
            status: 'timeout',
            duration_ms: Date.now() - connectionImportStartedAt,
          });
        }
        setPendingPluggyItemId(null);
        setConnectionImportStartedAt(null);
        setIsCreatingItem(false);
        setConnectionImportTimedOut(true);
      }
    };
    const interval = window.setInterval(() => {
      void poll();
    }, CONNECTION_IMPORT_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [connectionImportStartedAt, finishPendingImportIfPresent, loadData, pendingPluggyItemId, revalidateFinancialCaches]);

  const handleConnectionSuccess = useCallback(async (pluggyItemId: string) => {
    const normalizedId = pluggyItemId.trim().replace(/^['"]|['"]$/g, '');
    const isValidPluggyItemId =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(normalizedId);
    if (
      !normalizedId ||
      normalizedId.toLowerCase() === 'null' ||
      normalizedId.toLowerCase() === 'undefined' ||
      !isValidPluggyItemId
    ) {
      track('connect.widget_error', { stage: 'redirect' });
      setRedirectError('Falha ao completar a conexão. Tente conectar novamente.');
      setIsCreatingItem(false);
      setPendingPluggyItemId(null);
      setConnectionImportStartedAt(null);
      setConnectionImportTimedOut(false);
      return;
    }

    track('action.connect.complete', {});

    // A fresh attempt (including re-connecting the same item) gets its own
    // terminal import_finished signal.
    importOutcomeTrackedRef.current = null;

    // Start the page-level import progress after Pluggy completes.
    const importStartedAt = Date.now();
    setConnectionImportTimedOut(false);
    setCompletedConnectionName(null);
    setPendingPluggyItemId(normalizedId);
    setConnectionImportStartedAt(importStartedAt);
    setIsCreatingItem(true);
    setError(null);
    setRedirectError(null);
    try {
      const loadedItems = await loadData({ silent: true });
      if (finishPendingImportIfPresent(loadedItems, normalizedId, importStartedAt)) {
        void revalidateFinancialCaches();
      }
    } catch (err) {
      console.error('Error refreshing items after Pluggy success:', err);
      setError(err instanceof Error ? err.message : 'Erro ao atualizar conexões');
    }
  }, [finishPendingImportIfPresent, loadData, revalidateFinancialCaches]);

  const handleDisconnect = useCallback(async (itemId: string) => {
    setIsDisconnecting(true);
    try {
      await deleteItem(itemId);
      track('action.connect.disconnect');
      showToast('Conta desconectada com sucesso.', 'success');
      setDisconnectingItem(null);
      await loadData();
      void revalidateFinancialCaches();
    } catch (err) {
      console.error('Error disconnecting:', err);
      notifyError('connections', err, 'Não foi possível desconectar a conta.');
    } finally {
      setIsDisconnecting(false);
    }
  }, [deleteItem, loadData, showToast, revalidateFinancialCaches]);

  const openConnectFlow = useCallback(
    (
      item: ItemWithAccounts | null,
      reason: ConnectFlowState['reason'],
    ) => {
      track('action.connect.start', { reason: reason ?? 'new' });
      setConnectFlow({
        open: true,
        itemId: item?.pluggyItemId,
        connectorName: item?.connectorName,
        connectorImage: item?.connectorImage,
        reason,
      });
    },
    [],
  );

  const pendingImportedItem = pendingPluggyItemId
    ? items.find((item) => item.pluggyItemId === pendingPluggyItemId) ?? null
    : null;
  const activeSyncItem = items.find((item) => item.status && ACTIVE_SYNC_STATUSES.includes(item.status)) ?? null;
  const actionRequiredItem = items.find((item) => item.status && ACTION_REQUIRED_STATUSES.includes(item.status)) ?? null;
  const loginErrorItem = items.find((item) => item.status === 'LOGIN_ERROR') ?? null;

  const importVisible =
    !!pendingPluggyItemId ||
    !!activeSyncItem ||
    !!actionRequiredItem ||
    !!loginErrorItem ||
    !!completedConnectionName ||
    connectionImportTimedOut ||
    !!redirectError;

  const importConnectorName =
    pendingImportedItem?.connectorName ??
    activeSyncItem?.connectorName ??
    actionRequiredItem?.connectorName ??
    loginErrorItem?.connectorName ??
    completedConnectionName ??
    connectFlow.connectorName ??
    null;
  const importConnectorImage =
    pendingImportedItem?.connectorImage ??
    activeSyncItem?.connectorImage ??
    actionRequiredItem?.connectorImage ??
    loginErrorItem?.connectorImage ??
    connectFlow.connectorImage ??
    null;

  const handleTimeoutRefresh = useCallback(() => {
    setRedirectError(null);
    void loadData({ silent: true }).then((loadedItems) => {
      if (loadedItems.length > 0) {
        setConnectionImportTimedOut(false);
        void revalidateFinancialCaches();
      }
    });
  }, [loadData, revalidateFinancialCaches]);

  const handleReauthFromImport = useCallback(() => {
    if (loginErrorItem) openConnectFlow(loginErrorItem, 'reauth');
    else if (actionRequiredItem) openConnectFlow(actionRequiredItem, 'update');
  }, [actionRequiredItem, loginErrorItem, openConnectFlow]);

  const importState: ImportState = useMemo(() => {
    if (!importVisible) {
      return { active: false, steps: [] };
    }

    const steps = deriveImportSteps({
      pendingPluggyItemId,
      pendingItemInDb: pendingImportedItem,
      activeSync: !!activeSyncItem,
      completed: !!completedConnectionName && !activeSyncItem && !actionRequiredItem && !loginErrorItem,
      actionRequired: !!actionRequiredItem,
      errored: !!loginErrorItem || !!redirectError,
      timedOut: connectionImportTimedOut,
    });

    let alert;
    if (redirectError) {
      alert = buildErrorAlert(redirectError, handleTimeoutRefresh);
    } else if (loginErrorItem) {
      alert = buildErrorAlert(
        loginErrorItem.errorMessage || loginErrorItem.errorCode || null,
        handleReauthFromImport,
      );
    } else if (actionRequiredItem) {
      alert = buildActionRequiredAlert(null, handleReauthFromImport);
    } else if (connectionImportTimedOut) {
      alert = buildTimeoutAlert(handleTimeoutRefresh);
    }

    return { active: true, steps, alert };
  }, [
    importVisible,
    pendingPluggyItemId,
    pendingImportedItem,
    activeSyncItem,
    completedConnectionName,
    actionRequiredItem,
    loginErrorItem,
    redirectError,
    connectionImportTimedOut,
    handleTimeoutRefresh,
    handleReauthFromImport,
  ]);

  // Auto-close the dialog ~4s after the import completes successfully.
  useEffect(() => {
    if (!connectFlow.open) return;
    if (!importState.active) return;
    const allDone = importState.steps.every((s) => s.state === 'done');
    if (!allDone) return;
    const t = window.setTimeout(() => {
      setConnectFlow((s) => ({ ...s, open: false }));
    }, 4000);
    return () => window.clearTimeout(t);
  }, [connectFlow.open, importState]);

  if (isLoading && items.length === 0 && !isCreatingItem && !(redirectError || error)) {
    return <PageSkeleton />;
  }

  if (error && items.length === 0) {
    return (
      <ErrorState
        title="Erro ao carregar conexões"
        message="Não conseguimos carregar suas conexões agora. Verifique sua internet e tente novamente."
        onAction={() => {
          void loadData();
        }}
      />
    );
  }

  return (
    <div data-tokens="v2" className="space-y-6" data-onboarding-connections-page>
      <PageHeader
        title="Conexões Open Finance"
        description="Conecte, atualize e revogue acessos bancários com segurança."
        titleClassName="font-display text-2xl font-semibold tracking-normal sm:text-3xl"
        actions={
          <Button onClick={() => openConnectFlow(null, 'new')} size="sm" data-onboarding-connection-cta>
            <PlusIcon size={16} weight="bold" />
            Conectar conta
          </Button>
        }
      />

      {items.length > 0 && <ConnectionsMetrics items={items} />}

      {importState.active && !connectFlow.open && (
        <ImportProgress
          variant="horizontal"
          steps={importState.steps}
          alert={importState.alert}
          connectorName={importConnectorName}
          connectorImage={importConnectorImage}
        />
      )}

      {items.length === 0 && !importState.active ? (
        <EmptyState isCreating={isCreatingItem} />
      ) : items.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {items.map((item) => (
            <ConnectionCard
              key={item.id}
              item={item}
              onReauth={() => openConnectFlow(item, 'reauth')}
              onRenew={() => openConnectFlow(item, 'renew')}
              onUpdate={() => openConnectFlow(item, 'update')}
              onDisconnect={() => setDisconnectingItem(item)}
            />
          ))}
        </div>
      ) : null}

      <ConnectionUpdateDialog
        state={connectFlow}
        importState={importState}
        onOpenChange={(open) => setConnectFlow((s) => ({ ...s, open }))}
        onSuccess={handleConnectionSuccess}
      />

      <Dialog
        open={disconnectingItem !== null}
        onOpenChange={(open) => {
          if (!open && !isDisconnecting) setDisconnectingItem(null);
        }}
      >
        <DialogContent className="max-w-lg" hideCloseButton={isDisconnecting}>
          <DialogHeader>
            <DialogTitle>Desconectar instituição</DialogTitle>
            <DialogDescription>
              {disconnectingItem
                ? `Você vai remover a conexão com ${disconnectingItem.connectorName}. Você poderá reconectar depois, mas os dados deixam de atualizar até lá.`
                : 'Confirme para remover esta conexão.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="justify-end">
            <Button variant="secondary" onClick={() => setDisconnectingItem(null)} disabled={isDisconnecting}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => disconnectingItem && void handleDisconnect(disconnectingItem.id)}
              disabled={isDisconnecting}
            >
              {isDisconnecting ? (
                <>
                  <CircleNotchIcon size={16} className="animate-spin" />
                  Desconectando...
                </>
              ) : (
                'Desconectar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmptyState({ isCreating }: { isCreating: boolean }) {
  return (
    <Card variant="flat" className="rounded-[var(--radius-3xl)] border-border bg-surface p-5 shadow-[var(--shadow-1)] sm:p-6">
      <div className="mx-auto flex max-w-xl flex-col items-center gap-4 text-center">
        <div className="flex size-12 items-center justify-center rounded-[var(--radius-xl)] bg-surface-sunken text-brand-primary">
          {isCreating ? (
            <CircleNotchIcon size={24} className="animate-spin" />
          ) : (
            <CreditCardIcon size={24} weight="duotone" />
          )}
        </div>
        <h3 className="text-lg font-semibold text-text-primary">
          {isCreating ? 'Conexão recebida' : 'Conecte seu primeiro banco'}
        </h3>
        <p className="text-sm/6 text-text-secondary">
          {isCreating
            ? 'Estamos aguardando o retorno da instituição para importar contas, transações e investimentos.'
            : 'Trazemos saldos, transações, faturas e investimentos das suas contas em segundos — via Open Finance.'}
        </p>
      </div>
    </Card>
  );
}
